import { useEffect, useState } from "react";
import workOrderService from "../../../services/workOrderService";
import File from "../../../models/file";
import GLightbox from "glightbox";
import ModalWindow from "../../common/ModalWindow";
import urlHelper from "../../../util/urlHelper";
import WorkOrderFileShareWindow from "./WorkOrderFileShareWindow";
import WorkOrder from "../../../models/workOrder";
import workOrderHelper from "../../../models/workOrderHelper";

const WorkOrderFileList: React.FC<{
  idSubscription: number;
  workOrder: WorkOrder;
  hasShare: boolean;
  hasDelete: boolean;
  refreshImageListIndex?: number;
  onSelectedFilesChanged?: any;
}> = ({
  idSubscription,
  workOrder,
  hasShare,
  hasDelete,
  refreshImageListIndex = 0,
  onSelectedFilesChanged,
}) => {
  const [files, setFiles] = useState<File[]>([]);

  const [showNoFiles, setShowNoFiles] = useState<boolean>(false);

  const [showSelectFiles, setShowSelectFiles] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [internalRefreshImageListIndex, setInternalRefreshImageListIndex] =
    useState<number>(0);

  const [
    showDeleteFileConfirmationWindow,
    setShowDeleteFileConfirmationWindow,
  ] = useState<boolean>(false);

  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);

  useEffect(() => {
    if (!files.length) {
      return;
    }

    const lightbox = GLightbox({
      selector: ".glightbox",
      loop: true,
      zoomable: true,
    });

    return () => {
      lightbox.destroy();
    };
  }, [files.length]);

  useEffect(() => {
    if (!workOrder.workOrderNumber) {
      return;
    }

    const init = async () => {
      const { data: workOrderFiles } = await workOrderService.getWorkOrderFiles(
        idSubscription,
        workOrder.workOrderNumber,
      );

      setFiles(workOrderFiles);

      setShowNoFiles(workOrderFiles.length === 0);
    };

    init();
  }, [
    idSubscription,
    workOrder,
    refreshImageListIndex,
    internalRefreshImageListIndex,
  ]);

  const onSelectFilesClicked = () => {
    const newState = !showSelectFiles;

    setShowSelectFiles(newState);

    if (!newState) {
      setSelectedFilesAndNotify([]);
    }
  };

  const onSelectFile = (e: any, file: File) => {
    const checked = e.target.checked;
    if (checked) {
      const newSelectedFiles = [...selectedFiles];
      newSelectedFiles.push(file);
      setSelectedFilesAndNotify(newSelectedFiles);
    } else {
      const newSelectedFiles = selectedFiles.filter((f) => f !== file);
      setSelectedFilesAndNotify(newSelectedFiles);
    }
  };

  const onDeleteFiles = async () => {
    setIsBusy(true);

    for (const file of selectedFiles) {
      await workOrderService.deleteWorkOrderFile(
        idSubscription,
        workOrder.workOrderNumber,
        file.resourceId,
      );
    }

    setSelectedFilesAndNotify([]);
    setShowSelectFiles(false);

    await loadFiles();

    setIsBusy(false);
  };

  const setSelectedFilesAndNotify = (files: File[]) => {
    setSelectedFiles(files);

    if (onSelectedFilesChanged) {
      onSelectedFilesChanged(files);
    }
  };

  const loadFiles = async () => {
    const { data: workOrderFiles } = await workOrderService.getWorkOrderFiles(
      idSubscription,
      workOrder.workOrderNumber,
    );

    setFiles(workOrderFiles);

    setShowNoFiles(workOrderFiles.length === 0);
  };

  const getSharedToList = (file: File) => {
    let sharedWithMessage = "Shared with ";

    sharedWithMessage += file.workOrderFileShares
      .map((share) => share.toWorkOrder)
      .map((wo) => workOrderHelper.getWorOrderDisplayName(wo))
      .join(", ");

    return sharedWithMessage;
  };

  const getSharedFrom = (file: File) => {
    const fromWorkOrder = file.workOrderFileShares.find(
      (share) => share.fromWorkOrder !== null,
    )?.fromWorkOrder;

    return "Shared by " + workOrderHelper.getWorOrderDisplayName(fromWorkOrder);
  };

  const getSharedForwardToList = (file: File) => {
    let sharedWithMessage = "Shared Forward with ";

    const sharedFwToList = file.workOrderFileShares.filter(
      (share) => share.toWorkOrder !== null,
    );

    const workOrders = sharedFwToList.map((share) => share.toWorkOrder);

    sharedWithMessage += workOrders
      .map((wo) => workOrderHelper.getWorOrderDisplayName(wo))
      .join(", ");

    return sharedWithMessage;
  };

  return (
    <div>
      {showNoFiles && <div>No Files</div>}
      {!showNoFiles && (
        <div className="mb-4">
          <button
            type="button"
            className="btn btn-primary"
            data-bs-toggle="button"
            onClick={() => onSelectFilesClicked()}
            disabled={isBusy}
          >
            Select Files
          </button>
          {showSelectFiles && (
            <span className="ms-1">{selectedFiles.length} selected files</span>
          )}
          {hasShare && (
            <button
              type="button"
              className="btn btn-primary ms-4"
              disabled={isBusy || selectedFiles.length === 0}
              onClick={() => setShowShareWindow(true)}
            >
              Share Files
            </button>
          )}
          {hasDelete && (
            <button
              type="button"
              className="btn btn-primary ms-2"
              disabled={isBusy || selectedFiles.length === 0}
              onClick={() => setShowDeleteFileConfirmationWindow(true)}
            >
              Delete Files
            </button>
          )}
        </div>
      )}
      <div className="gallery">
        {files.map((file: File, index) => (
          <div key={index.toString()}>
            <div style={{ position: "relative" }}>
              <div
                style={{
                  width: "200px",
                  height: "150px",
                  overflow: "hidden",
                }}
              >
                {file.fileContentType.includes("image") && (
                  <a
                    href={urlHelper.safeEncodeUrl(file.fileUrl)} // full size
                    className="glightbox"
                    data-title={file.fileName}
                  >
                    <img
                      src={urlHelper.safeEncodeUrl(file.fileUrl)} // thumb
                      alt={file.fileName}
                      style={{ cursor: "pointer", height: "100%" }}
                    />
                  </a>
                )}
                {file.fileContentType.includes("video") && (
                  <a
                    href={urlHelper.safeEncodeUrl(file.fileUrl)}
                    className="glightbox"
                    data-title={file.fileName}
                    data-type="video"
                  >
                    <div className="video-wrapper">
                      <video className="video" muted preload="metadata">
                        <source src={urlHelper.safeEncodeUrl(file.fileUrl)} />
                      </video>
                      <button className="play-button"></button>
                    </div>
                  </a>
                )}
                {!file.fileContentType.includes("image") &&
                  !file.fileContentType.includes("video") && (
                    <div
                      className="file-thumb"
                      onClick={() =>
                        window.open(
                          urlHelper.safeEncodeUrl(file.fileUrl),
                          "_blank",
                        )
                      }
                    >
                      <i
                        className="bi bi-file-earmark"
                        style={{ fontSize: "30px" }}
                      ></i>
                      <span className="file-ext">
                        {file.fileExtension.replace(".", "").toUpperCase()}
                      </span>
                    </div>
                  )}
              </div>
              <div
                className="d-flex justify-content-end"
                style={{
                  width: "200px",
                  position: "absolute",
                  bottom: "3px",
                  left: "-3px",
                }}
              >
                {file.isShared && file.isOwned && (
                  <>
                    <div className="badge bg-primary">Owned</div>
                    <div
                      className="badge bg-primary ms-2"
                      title={getSharedToList(file)}
                    >
                      Shared
                    </div>
                  </>
                )}
                {file.isShared && !file.isOwned && (
                  <div className="badge bg-primary" title={getSharedFrom(file)}>
                    Shared
                  </div>
                )}
                {file.isSharedForward && (
                  <div
                    className="badge bg-primary ms-2"
                    title={getSharedForwardToList(file)}
                  >
                    Shared Forward
                  </div>
                )}
              </div>
            </div>
            {/* image name & select button */}
            <div
              className="d-flex justify-content-between align-items-start"
              style={{ width: "200px" }}
            >
              <div className="break-text">{file.fileName}</div>
              {showSelectFiles && (
                <div>
                  <input
                    type="checkbox"
                    className="form-check-input highlight"
                    onChange={(e) => {
                      onSelectFile(e, file);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {showDeleteFileConfirmationWindow && (
        <ModalWindow
          title="Delete Files"
          onOk={async () => {
            await onDeleteFiles();
            setShowDeleteFileConfirmationWindow(false);
          }}
          onCancel={() => {
            setShowDeleteFileConfirmationWindow(false);
          }}
        >
          <div>
            <div className="mb-2">
              <div>Confirm delete {selectedFiles.length} selected files?</div>
            </div>
          </div>
        </ModalWindow>
      )}
      {showShareWindow && (
        <WorkOrderFileShareWindow
          idSubscription={idSubscription}
          selectedFiles={selectedFiles}
          setShowShareWindow={setShowShareWindow}
          workOrder={workOrder}
          onOk={() => setInternalRefreshImageListIndex((prev) => prev + 1)}
        />
      )}
    </div>
  );
};

export default WorkOrderFileList;
